import { JobType, JobCategory, JobStatus, ConnectProto } from "@racoon/api";
import { Namespace } from "@racoon/i18n";

/** Job type → display label with i18n (Namespace.Orders). Reusable on any page. */
export const getTypeMap = (t: any): Partial<Record<JobType, string>> => ({
  [JobType.JOB_TYPE_ADD_BUNKER]: t(Namespace.Orders, "types.ADD_BUNKER") || "Установка бункера",
  [JobType.JOB_TYPE_REPLACE_BUNKER]:
    t(Namespace.Orders, "types.REPLACE_BUNKER") || "Поменять бункер",
  [JobType.JOB_TYPE_UNDEFINED]: t(Namespace.Orders, "types.UNDEFINED") || "Не определен",
  [JobType.JOB_TYPE_REMOVE_BUNKER]: t(Namespace.Orders, "types.REMOVE_BUNKER") || "Забрать бункер",
  [JobType.JOB_TYPE_MSW]: t(Namespace.Orders, "types.MSW") || "Вывоз ТБО",
  [JobType.JOB_TYPE_MSW_CLEANUP]: t(Namespace.Orders, "types.MSW_CLEANUP") || "Подбор",
  [JobType.JOB_TYPE_MSW_MOBILE_CLEANUP]:
    t(Namespace.Orders, "types.MSW_MOBILE_CLEANUP") || "Подбор с объездом на ходу",
  [JobType.JOB_TYPE_DISINFECTION]: t(Namespace.Orders, "types.DISINFECTION") || "Дезинфекция",
  [JobType.JOB_TYPE_LIQUID_WASTE]: t(Namespace.Orders, "types.LIQUID_WASTE") || "Вывоз ЖБО",
  [JobType.JOB_TYPE_RECEIVE_LOAD]: t(Namespace.Orders, "types.RECEIVE_LOAD") || "Приём груза",
  // Multilift (double container) variants of the core bunker operations.
  [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_REPLACE_BUNKER]: t(
    Namespace.Enum,
    "JOB_TYPE_DOUBLE_CONSTRUCTION_REPLACE_BUNKER",
  ),
  [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_ADD_BUNKER]: t(
    Namespace.Enum,
    "JOB_TYPE_DOUBLE_CONSTRUCTION_ADD_BUNKER",
  ),
  [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_REMOVE_BUNKER]: t(
    Namespace.Enum,
    "JOB_TYPE_DOUBLE_CONSTRUCTION_REMOVE_BUNKER",
  ),
  [JobType.UNRECOGNIZED]: t(Namespace.Orders, "types.UNDEFINED") || "Не определен",
});

/**
 * Job status → display label with i18n (Namespace.Enum). Reusable on any page.
 *
 * UNDEFINED/UNRECOGNIZED are absent (nothing to pick), and so is FORCE_COMPLETED —
 * it shares the "Выполнено" label with COMPLETED, so filters fold it into that
 * option (see JOB_STATUS_GROUPS) instead of showing the same label twice.
 */
export const getJobStatusMap = (t: any): Partial<Record<JobStatus, string>> => ({
  [JobStatus.JOB_STATUS_CREATED]: t(Namespace.Enum, "JOB_STATUS_CREATED") || "Неназначено",
  [JobStatus.JOB_STATUS_ASSIGNED]: t(Namespace.Enum, "JOB_STATUS_ASSIGNED") || "Назначено",
  [JobStatus.JOB_STATUS_ENROUTE]: t(Namespace.Enum, "JOB_STATUS_ENROUTE") || "Машина в пути",
  [JobStatus.JOB_STATUS_ON_SERVICE]: t(Namespace.Enum, "JOB_STATUS_ON_SERVICE") || "Исполняется",
  [JobStatus.JOB_STATUS_COMPLETED]: t(Namespace.Enum, "JOB_STATUS_COMPLETED") || "Выполнено",
  [JobStatus.JOB_STATUS_NOT_COMPLETED]:
    t(Namespace.Enum, "JOB_STATUS_NOT_COMPLETED") || "Невывоз",
  [JobStatus.JOB_STATUS_CANCELLED]: t(Namespace.Enum, "JOB_STATUS_CANCELLED") || "Отменено",
});

/** Job category → display label with i18n (Namespace.Enum). Reusable on any page. */
export const getJobCategoryMap = (t: any): Record<JobCategory, string> => ({
  [JobCategory.JOB_CATEGORY_CO]: t(Namespace.Enum, "CATEGORY_CO") || "СО",
  [JobCategory.JOB_CATEGORY_MSW]: t(Namespace.Enum, "CATEGORY_MSW") || "ТБО",
  [JobCategory.JOB_CATEGORY_LIQUID]: t(Namespace.Enum, "CATEGORY_LIQUID") || "ЖБО",
  [JobCategory.JOB_CATEGORY_DISINFECTION]:
    t(Namespace.Enum, "CATEGORY_DISINFECTION") || "Дезинфекция",
  [JobCategory.JOB_CATEGORY_TIPPER]: t(Namespace.Enum, "CATEGORY_TIPPER") || "Мультилифт",
  [JobCategory.JOB_CATEGORY_UNDEFINED]: t(Namespace.Enum, "CATEGORY_UNDEFINED") || "Не определен",
  [JobCategory.UNRECOGNIZED]: t(Namespace.Enum, "CATEGORY_UNDEFINED") || "Не определен",
});

/**
 * Job type → i18n key (Namespace.Enum) of the category it belongs to, so pickers can show
 * "СО / ТБО / Дезинфекция / Мультилифт / ЖБО" next to the job type. Job types without a
 * category (Перевозка, работы с ёмкостью, …) are intentionally absent.
 */
export const JOB_TYPE_CATEGORY_KEY: Partial<Record<ConnectProto.JobType, string>> = {
  [ConnectProto.JobType.REPLACE_BUNKER]: "CATEGORY_CO",
  [ConnectProto.JobType.ADD_BUNKER]: "CATEGORY_CO",
  [ConnectProto.JobType.REMOVE_BUNKER]: "CATEGORY_CO",
  [ConnectProto.JobType.RECEIVE_LOAD]: "CATEGORY_CO",
  [ConnectProto.JobType.MSW]: "CATEGORY_MSW",
  [ConnectProto.JobType.MSW_CLEANUP]: "CATEGORY_MSW",
  [ConnectProto.JobType.MSW_MOBILE_CLEANUP]: "CATEGORY_MSW",
  [ConnectProto.JobType.DISINFECTION]: "CATEGORY_DISINFECTION",
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_REPLACE_BUNKER]: "CATEGORY_TIPPER",
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_ADD_BUNKER]: "CATEGORY_TIPPER",
  [ConnectProto.JobType.DOUBLE_CONSTRUCTION_REMOVE_BUNKER]: "CATEGORY_TIPPER",
  [ConnectProto.JobType.LIQUID_WASTE]: "CATEGORY_LIQUID",
};

/** Category order used when a picker groups job types by category. */
export const JOB_TYPE_CATEGORY_ORDER: string[] = [
  "CATEGORY_CO",
  "CATEGORY_MSW",
  "CATEGORY_DISINFECTION",
  "CATEGORY_TIPPER",
  "CATEGORY_LIQUID",
];

/** Route category → display label with i18n (Namespace.Enum). Reusable on any page. */
export const getRouteCategoryMap = (t: any): Record<ConnectProto.RouteCategory, string> => ({
  [ConnectProto.RouteCategory.CO]: t(Namespace.Enum, "CATEGORY_CO") || "СО",
  [ConnectProto.RouteCategory.MSW]: t(Namespace.Enum, "CATEGORY_MSW") || "ТБО",
  [ConnectProto.RouteCategory.LIQUID]: t(Namespace.Enum, "CATEGORY_LIQUID") || "ЖБО",
  [ConnectProto.RouteCategory.DISINFECTION]:
    t(Namespace.Enum, "CATEGORY_DISINFECTION") || "Дезинфекция",
  [ConnectProto.RouteCategory.TIPPER]: t(Namespace.Enum, "CATEGORY_TIPPER") || "Мультилифт",
  [ConnectProto.RouteCategory.UNDEFINED]: t(Namespace.Enum, "CATEGORY_UNDEFINED") || "Не определен",
});

/** Route status → display label with i18n (Namespace.Enum). Reusable on any page. */
export const getRouteStatusMap = (t: any): Record<ConnectProto.RouteStatus, string> => ({
  [ConnectProto.RouteStatus.CREATED]: t(Namespace.Enum, "ROUTE_STATUS_CREATED") || "Назначено",
  [ConnectProto.RouteStatus.IN_PROGRESS]:
    t(Namespace.Enum, "ROUTE_STATUS_IN_PROGRESS") || "В процессе",
  [ConnectProto.RouteStatus.RETURNING]:
    t(Namespace.Enum, "ROUTE_STATUS_RETURNING") || "Возвращается",
  [ConnectProto.RouteStatus.COMPLETED]: t(Namespace.Enum, "ROUTE_STATUS_COMPLETED") || "Выполнено",
  [ConnectProto.RouteStatus.CANCELLED]: t(Namespace.Enum, "ROUTE_STATUS_CANCELLED") || "Отменено",
  [ConnectProto.RouteStatus.FORCE_COMPLETED]:
    t(Namespace.Enum, "ROUTE_STATUS_FORCE_COMPLETED") || "Выполнено",
  [ConnectProto.RouteStatus.NOT_APPROVED]:
    t(Namespace.Enum, "ROUTE_STATUS_NOT_APPROVED") || "Неутверждено",
  [ConnectProto.RouteStatus.UNDEFINED]:
    t(Namespace.Enum, "ROUTE_STATUS_UNDEFINED") || "Не определено",
});
